import React, { Suspense, useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import { clientApi, JobStatus, ReviewPatch } from '@/api/client'
import { qk } from '@/api/queryKeys'
import { streamSse } from '@/api/sse'
import ErrorBox from '@/components/ErrorBox'
import { Chevron } from '@/components/Icon'

// Staff review panel is code-split so non-staff clients don't pay the
// bundle cost for the analyst workflow (textareas, review actions,
// AI-review renderer).
const StaffReviewPanel = React.lazy(() => import('./itemDetail/StaffReviewPanel'))
import Topbar from '@/components/Topbar'
import BackLink from '@/components/BackLink'
import ProbBar from '@/components/ProbBar'
import Delta from '@/components/Delta'
import Sparkline from '@/components/Sparkline'
import Skeleton from '@/components/Skeleton'
import RenderBoundary from '@/components/RenderBoundary'
import ScenarioTracks from '@/components/ScenarioTracks'
import DownloadReportButton from '@/components/DownloadReportButton'
import { fmtProb, fmtEdge, fmtTs, timeAgo, cleanSnippet, cleanSourceSnippet } from '@/utils/format'

/* ---------- Agent card (inline sub-component) ---------- */

/** Safely coerce any value to a renderable string. */
function str(v: any): string {
  if (typeof v === 'string') return v
  if (v == null) return ''
  return JSON.stringify(v)
}

/** Try to parse a JSON string into an object; return original value on failure.
 *  Also handles truncated JSON (e.g. `{"driver":"X","description":"text...`)
 *  by extracting field values with regex when JSON.parse fails. */
function tryParseJson(val: any): any {
  if (typeof val !== 'string') return val
  const t = val.trim()
  if (t[0] !== '{') return val
  // Try strict parse first
  if (t[t.length - 1] === '}') {
    try { return JSON.parse(t) } catch { /* fall through to regex extraction */ }
  }
  // Regex extraction for truncated JSON objects
  const fields: Record<string, string> = {}
  const re = /"(\w+)"\s*:\s*"((?:[^"\\]|\\.)*)(?:"|$)/g
  let m: RegExpExecArray | null
  while ((m = re.exec(t)) !== null) {
    fields[m[1]] = m[2].replace(/\\"/g, '"').replace(/\\n/g, '\n')
  }
  if (Object.keys(fields).length > 0) return fields
  return val
}

function renderStructuredItem(item: any) {
  const obj = tryParseJson(item)
  if (typeof obj === 'string') return obj
  if (typeof obj !== 'object' || !obj) return JSON.stringify(item)
  const name = obj.driver || obj.uncertainty || obj.factor || obj.question || obj.sub_question || obj.title || obj.name
  const desc = obj.description || obj.summary || obj.snippet
  if (name && desc) return <><span style={{ fontWeight: 600 }}>{str(name)}</span> &mdash; <span className="muted">{str(desc)}</span></>
  if (name) return <span style={{ fontWeight: 600 }}>{str(name)}</span>
  if (desc) return <>{str(desc)}</>
  return JSON.stringify(obj)
}

function renderTextOrList(val: any, label?: string) {
  if (!val) return null
  if (Array.isArray(val)) {
    const items = val.filter(Boolean)
    if (!items.length) return null
    return (
      <div style={{ marginTop: 6 }}>
        {label && <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', marginBottom: 2 }}>{label}</div>}
        <ul style={{ margin: '2px 0 0 16px', padding: 0, fontSize: 12, lineHeight: 1.5 }}>
          {items.slice(0, 12).map((item: any, i: number) => (
            <li key={i} style={{ marginBottom: 3 }}>
              {typeof item === 'string' ? item : renderStructuredItem(item)}
            </li>
          ))}
        </ul>
      </div>
    )
  }
  return (
    <div style={{ fontSize: 12, lineHeight: 1.5, marginTop: 4 }}>
      {label && <span style={{ fontWeight: 700 }}>{label}: </span>}
      {String(val)}
    </div>
  )
}

function AgentCard({ agent }: { agent: any }) {
  const [open, setOpen] = useState(false)
  const output = agent.output || {}
  const hasForecastProb = typeof agent.prob === 'number'

  return (
    <div className={`agent-card${hasForecastProb ? '' : ' agent-card--utility'}`}>
      <div
        className="agent-card-header"
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen((o) => !o) }
        }}
        role="button"
        tabIndex={0}
        aria-expanded={open}
        aria-label={`${agent.name} agent details`}
        style={{ cursor: 'pointer' }}
      >
        <div style={{ flex: 1 }}>
          <span style={{ fontWeight: 700, fontSize: 13 }}>{agent.name}</span>
          {hasForecastProb && (
            <span style={{ marginLeft: 10, fontFamily: 'var(--mono)', fontWeight: 700, color: 'var(--accent)' }}>
              {fmtProb(agent.prob)}
            </span>
          )}
          {typeof agent.confidence === 'number' && (
            <span className="muted" style={{ marginLeft: 8, fontSize: 11 }}>
              conf: {(agent.confidence * 100).toFixed(0)}%
            </span>
          )}
        </div>
        <span className="muted" style={{ fontSize: 11, flexShrink: 0, display: 'inline-flex', alignItems: 'center' }}>
          <Chevron open={open} ariaLabel={open ? 'Collapse' : 'Expand'} />
        </span>
      </div>
      {agent.summary && !open && (
        <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>{String(agent.summary).slice(0, 160)}</div>
      )}
      {open && (
        <div className="agent-card-detail">
          {typeof output.reasoning === 'string' && output.reasoning && (
            <div style={{ fontSize: 12, lineHeight: 1.6, marginTop: 6, whiteSpace: 'pre-wrap' }}>{output.reasoning}</div>
          )}
          {!output.reasoning && agent.summary && (
            <div style={{ fontSize: 12, lineHeight: 1.6, marginTop: 6 }}>{agent.summary}</div>
          )}
          {renderTextOrList(output.key_points, 'Key Points')}
          {renderTextOrList(output.drivers, 'Drivers')}
          {renderTextOrList(output.uncertainties, 'Uncertainties')}
          {renderTextOrList(output.key_uncertainties, 'Key Uncertainties')}
          {output.confidence_killer && (
            <div style={{ marginTop: 6, fontSize: 12 }}>
              <span style={{ fontWeight: 700, color: 'var(--warn)' }}>Confidence killer: </span>{output.confidence_killer}
            </div>
          )}
          {renderTextOrList(output.sub_questions, 'Sub-Questions')}
          {output.final_results && Array.isArray(output.final_results) && output.final_results.length > 0 && (
            <div style={{ marginTop: 6 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', marginBottom: 2 }}>Search Results</div>
              <ul style={{ margin: '2px 0 0 16px', padding: 0, fontSize: 12 }}>
                {output.final_results.slice(0, 8).map((r: any, i: number) => (
                  <li key={i} style={{ marginBottom: 3 }}>
                    <span style={{ fontWeight: 600 }}>{r.title || r.url}</span>
                    {r.snippet && <span className="muted"> &mdash; {String(r.snippet).slice(0, 120)}</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {output.sources && Array.isArray(output.sources) && output.sources.length > 0 && (
            <div style={{ marginTop: 8 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)' }}>Sources</div>
              <ul style={{ margin: '4px 0 0 16px', padding: 0, fontSize: 12 }}>
                {output.sources.slice(0, 8).map((src: any, i: number) => (
                  <li key={i} style={{ marginBottom: 2 }}>
                    {typeof src === 'string' ? src : src?.title || src?.url || JSON.stringify(src)}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {renderTextOrList(output.issues_found, 'Issues Found')}
          {renderTextOrList(output.logical_gaps, 'Logical Gaps')}
          {typeof output.suggested_adjustment === 'number' && (
            <div style={{ marginTop: 4, fontSize: 12 }}>
              <span style={{ fontWeight: 700 }}>Suggested adj: </span>
              <span style={{ fontFamily: 'var(--mono)' }}>
                {output.suggested_adjustment > 0 ? '+' : ''}{(output.suggested_adjustment * 100).toFixed(1)}%
              </span>
            </div>
          )}
          {typeof output.dissent_score === 'number' && (
            <div style={{ marginTop: 4, fontSize: 12 }}>
              <span style={{ fontWeight: 700 }}>Dissent: </span>
              <span style={{ fontFamily: 'var(--mono)' }}>{output.dissent_score.toFixed(2)}</span>
            </div>
          )}
          {output.llm_summary && (
            <div style={{ fontSize: 12, lineHeight: 1.5, marginTop: 6 }}>{output.llm_summary}</div>
          )}
        </div>
      )}
    </div>
  )
}

function formatAdjustment(value: any) {
  return typeof value === 'number' ? `${value >= 0 ? '+' : ''}${(value * 100).toFixed(1)} pts` : '-'
}

function CompactList({ items, max = 5 }: { items: any[]; max?: number }) {
  const rows = Array.isArray(items) ? items.filter(Boolean).slice(0, max) : []
  if (!rows.length) return null
  return (
    <ul style={{ margin: '4px 0 0 16px', padding: 0, fontSize: 12, lineHeight: 1.55 }}>
      {rows.map((item: any, i: number) => <li key={i}>{typeof item === 'string' ? item : renderStructuredItem(item)}</li>)}
    </ul>
  )
}

function WorkspaceReconciliationPanel({ workspace }: { workspace: any }) {
  if (!workspace || typeof workspace !== 'object') return null
  const critic = workspace.critic_validation || {}
  const premortem = workspace.premortem || {}
  const fermi = workspace.fermi_breakdown || {}
  const trace = workspace.adjustment_trace || {}
  const sourcePack = workspace.source_pack_snapshot || {}
  const forecastability = workspace.forecastability || {}
  const diagnostics = workspace.confidence_diagnostics || {}
  const criticIssues = Array.isArray(critic.issues_found) ? critic.issues_found : []
  const criticGaps = Array.isArray(critic.logical_gaps) ? critic.logical_gaps : []
  const premortemUncertainties = Array.isArray(premortem.key_uncertainties) ? premortem.key_uncertainties : []
  const fermiSubQuestions = Array.isArray(fermi.sub_questions) ? fermi.sub_questions : []
  const hasReconciliation = Boolean(
    workspace.reasoning_summary ||
    Object.keys(critic).length ||
    Object.keys(premortem).length ||
    Object.keys(fermi).length ||
    Object.keys(trace).length,
  )
  if (!hasReconciliation) return null
  const traceRows = [
    ['Raw agent ensemble', trace.raw_ensemble ?? workspace.raw_ensemble],
    ['After critic', trace.post_critic_ensemble],
    ['After superforecast review', trace.post_superforecaster_ensemble],
    ['After extremization', trace.post_extremization_prob],
    ['After historical calibration', trace.post_historical_cal_prob],
    ['After forecastability gate', trace.post_forecastability_prob],
    ['Final probability', trace.final_probability ?? workspace.probability],
  ].filter(([, value]) => typeof value === 'number')

  return (
    <div style={{ marginBottom: 14 }}>
      <div className="muted" style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', marginBottom: 6 }}>Reconciliation Review</div>
      <div style={{ padding: '10px 12px', borderRadius: 10, background: 'rgba(99,102,241,0.07)', border: '1px solid rgba(99,102,241,0.18)' }}>
        <div className="row" style={{ gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
          {workspace.reconciliation_batch_id && <span className="pill ok">critic/premortem/fermi batch</span>}
          {workspace.synthesis_mode && <span className="pill">deterministic synthesis</span>}
          {typeof workspace.raw_ensemble === 'number' && <span className="pill">raw ensemble {fmtProb(workspace.raw_ensemble)}</span>}
          {typeof critic.suggested_adjustment === 'number' && <span className="pill">critic {formatAdjustment(critic.suggested_adjustment)}</span>}
          {typeof workspace.spread === 'number' && <span className="pill">spread {(workspace.spread * 100).toFixed(1)}%</span>}
          {forecastability.tier && <span className="pill">forecastability {String(forecastability.tier)}</span>}
          {sourcePack.snapshot_id && <span className="pill">source snapshot</span>}
        </div>
        {workspace.reasoning_summary && <div style={{ fontSize: 12, lineHeight: 1.55, marginBottom: 10 }}>{workspace.reasoning_summary}</div>}

        {traceRows.length > 0 && (
          <div style={{ marginTop: 8 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', marginBottom: 4 }}>Probability path</div>
            <div className="tableWrap">
              <table className="table" style={{ fontSize: 12 }}>
                <tbody>
                  {traceRows.map(([label, value]: any[]) => (
                    <tr key={label}>
                      <td>{label}</td>
                      <td style={{ textAlign: 'right', fontFamily: 'var(--mono)', fontWeight: 700 }}>{fmtProb(value)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div className="twoCol" style={{ marginTop: 12 }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', marginBottom: 4 }}>Critic</div>
            {typeof critic.suggested_adjustment === 'number' && <div style={{ fontSize: 12 }}>Suggested adjustment: <strong>{formatAdjustment(critic.suggested_adjustment)}</strong></div>}
            {critic.counter_argument && <div style={{ fontSize: 12, lineHeight: 1.55, marginTop: 4 }}>{critic.counter_argument}</div>}
            <CompactList items={[...criticIssues, ...criticGaps]} max={5} />
          </div>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', marginBottom: 4 }}>Premortem</div>
            {premortem.success_scenario && <div style={{ fontSize: 12, lineHeight: 1.55 }}><strong>YES path: </strong>{String(premortem.success_scenario).slice(0, 420)}</div>}
            {premortem.failure_scenario && <div style={{ fontSize: 12, lineHeight: 1.55, marginTop: 5 }}><strong>NO path: </strong>{String(premortem.failure_scenario).slice(0, 420)}</div>}
            <CompactList items={premortemUncertainties} max={4} />
            {premortem.confidence_killer && premortem.confidence_killer !== 'Unknown' && <div style={{ fontSize: 12, marginTop: 4 }}><strong>Confidence killer: </strong>{premortem.confidence_killer}</div>}
          </div>
        </div>

        <div className="twoCol" style={{ marginTop: 12 }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', marginBottom: 4 }}>Fermi/Base-Rate Check</div>
            {typeof fermi.combined_estimate === 'number' && <div style={{ fontSize: 12 }}>Independent estimate: <strong>{fmtProb(fermi.combined_estimate)}</strong></div>}
            {fermi.reasoning && <div style={{ fontSize: 12, lineHeight: 1.55, marginTop: 4 }}>{String(fermi.reasoning).slice(0, 520)}</div>}
            <CompactList items={fermiSubQuestions} max={4} />
          </div>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', marginBottom: 4 }}>Evidence Diagnostics</div>
            <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
              {typeof sourcePack.verified_record_count === 'number' && <span className="pill">{sourcePack.verified_record_count} verified records</span>}
              {typeof sourcePack.official_source_count === 'number' && <span className="pill">{sourcePack.official_source_count} official sources</span>}
              {typeof workspace.verified_source_count === 'number' && <span className="pill">{workspace.verified_source_count} attached sources</span>}
              {typeof diagnostics.average_agent_self_reported_confidence === 'number' && <span className="pill">agent confidence {fmtProb(diagnostics.average_agent_self_reported_confidence)}</span>}
            </div>
            {sourcePack.policy && <div className="muted" style={{ fontSize: 11, marginTop: 6 }}>{String(sourcePack.policy).replaceAll('_', ' ')}</div>}
          </div>
        </div>
      </div>
    </div>
  )
}

function ForecastReasoningPanel({ detail }: { detail: any }) {
  if (!detail) return null
  const agents: any[] = detail.agents || []
  const forecastAgents = agents.filter((a: any) => typeof a.prob === 'number')
  const utilityAgents = agents.filter((a: any) => typeof a.prob !== 'number')
  const meta = detail.notes_parsed || {}
  const ci = meta.confidence_interval
  const review = meta.high_edge_review
  const workspaceForecast = detail.workspace_forecast || null

  return (
    <div className="forecast-detail-panel">
      <div className="row" style={{ gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
        {typeof detail.our_prob === 'number' && <span className="pill ok">Forecast: {fmtProb(detail.our_prob)}</span>}
        {typeof detail.market_prob === 'number' && <span className="pill">Market: {fmtProb(detail.market_prob)}</span>}
        {typeof detail.edge === 'number' && (
          <span className={`pill ${detail.edge > 0 ? 'ok' : detail.edge < -0.05 ? 'bad' : ''}`}>
            Edge: {detail.edge > 0 ? '+' : ''}{(detail.edge * 100).toFixed(1)}%
          </span>
        )}
        {ci && (
          <span className="pill">
            CI: {typeof ci.low === 'number' ? fmtProb(ci.low) : '?'}–{typeof ci.high === 'number' ? fmtProb(ci.high) : '?'}
          </span>
        )}
        {typeof meta.spread === 'number' && <span className="pill">Spread: {(meta.spread * 100).toFixed(1)}%</span>}
        {forecastAgents.length > 0 && <span className="pill">{forecastAgents.length} agent{forecastAgents.length !== 1 ? 's' : ''}</span>}
      </div>

      {review && typeof review === 'object' && review.adjustment_reason && (
        <div style={{ fontSize: 12, marginBottom: 10, padding: '8px 10px', borderRadius: 8, background: 'rgba(251, 191, 36, 0.08)', border: '1px solid rgba(251, 191, 36, 0.2)' }}>
          <span style={{ fontWeight: 700, color: 'var(--warn)' }}>Review: </span>{review.adjustment_reason}
        </div>
      )}

      <WorkspaceReconciliationPanel workspace={workspaceForecast} />

      {forecastAgents.length > 0 && (
        <div style={{ marginBottom: utilityAgents.length > 0 ? 12 : 0 }}>
          <div className="muted" style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', marginBottom: 6 }}>Forecasting Agents</div>
          <div className="stack" style={{ gap: 6 }}>
            {forecastAgents.map((a: any, i: number) => <AgentCard key={a.name || i} agent={a} />)}
          </div>
        </div>
      )}

      {utilityAgents.length > 0 && (
        <div>
          <div className="muted" style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', marginBottom: 6 }}>Analysis Agents</div>
          <div className="stack" style={{ gap: 6 }}>
            {utilityAgents.map((a: any, i: number) => <AgentCard key={a.name || i} agent={a} />)}
          </div>
        </div>
      )}

      {detail.started_at && (
        <div className="muted" style={{ fontSize: 11, marginTop: 10 }}>
          Run #{detail.run_id} &middot; {timeAgo(detail.started_at)}
        </div>
      )}
    </div>
  )
}

/**
 * Compact read-only card that surfaces the scenario's prompt-safe dossier
 * summary next to a conditional forecast, mirroring the "Analysis" / forecast
 * summary on the same page. This is the same brief the worker injects into
 * every conditional run that uses the scenario — showing it here means the
 * reader can audit "why does this forecast change under these assumptions?"
 * without leaving the item page.
 *
 * Falls back silently when there is no dossier yet (we don't want to nag the
 * user on every conditional item) and links out to the full scenario page
 * for the detailed view (history, refresh, re-forecast, etc.).
 */
function ScenarioDossierSummaryCard({ scenarioId }: { scenarioId: string }) {
  const q = useQuery({
    queryKey: qk.scenarios.dossier(scenarioId),
    queryFn: () => clientApi.getScenarioDossier(scenarioId),
    staleTime: 60_000,
  })

  if (q.isLoading) {
    return (
      <div className="card">
        <h2>Scenario dossier</h2>
        <div className="muted" style={{ fontSize: 12 }}>Loading…</div>
      </div>
    )
  }

  const dossier = q.data?.dossier
  const summary = dossier?.prompt_safe_summary?.trim()
  const keyPoints = (dossier?.prompt_safe_key_points || []).filter(Boolean)
  const drivers = (dossier?.prompt_safe_drivers || []).filter(Boolean)
  const inhibitors = (dossier?.prompt_safe_inhibitors || []).filter(Boolean)

  const hasAny =
    !!summary || keyPoints.length > 0 || drivers.length > 0 || inhibitors.length > 0
  if (!hasAny) return null

  const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <div
      style={{
        fontSize: 11,
        fontWeight: 800,
        textTransform: 'uppercase',
        color: 'var(--muted)',
        marginBottom: 4,
      }}
    >
      {children}
    </div>
  )

  const Bullets: React.FC<{ items: string[]; max?: number }> = ({ items, max = 3 }) => (
    <ul style={{ margin: '0 0 0 18px', padding: 0, fontSize: 13, lineHeight: 1.55 }}>
      {items.slice(0, max).map((t, i) => (
        <li key={i}>{t}</li>
      ))}
    </ul>
  )

  return (
    <div className="card">
      <div
        className="row"
        style={{ justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}
      >
        <h2 style={{ margin: 0 }}>Scenario dossier</h2>
        <Link to={`/universe/scenarios/${scenarioId}`} style={{ fontSize: 12 }}>
          View full dossier →
        </Link>
      </div>
      <div className="muted" style={{ fontSize: 12, marginTop: 4, marginBottom: 10 }}>
        Shared brief injected into every conditional run for this scenario. This is what the
        forecasting agents read before producing the conditional probability above.
      </div>

      <div className="stack" style={{ gap: 12 }}>
        {summary && (
          <div>
            <SectionTitle>Summary</SectionTitle>
            <div style={{ lineHeight: 1.55 }}>{summary}</div>
          </div>
        )}

        {keyPoints.length > 0 && (
          <div>
            <SectionTitle>Key assumption entailments</SectionTitle>
            <Bullets items={keyPoints} />
          </div>
        )}

        {drivers.length > 0 && (
          <div>
            <SectionTitle>Drivers (push more extreme)</SectionTitle>
            <Bullets items={drivers} />
          </div>
        )}

        {inhibitors.length > 0 && (
          <div>
            <SectionTitle>Inhibitors (push less extreme)</SectionTitle>
            <Bullets items={inhibitors} />
          </div>
        )}

        {dossier?.updated_at && (
          <div className="muted" style={{ fontSize: 11 }}>
            Last refreshed {timeAgo(dossier.updated_at)}
            {dossier.last_refresh_run_id ? ` · triggered by run #${dossier.last_refresh_run_id}` : ''}
          </div>
        )}
      </div>
    </div>
  )
}

export default function ItemDetail() {
  const { itemId } = useParams()
  const queryClient = useQueryClient()
  const [showContract, setShowContract] = useState(false)
  const [runDetail, setRunDetail] = useState<any>(null)
  const [runDetailLoading, setRunDetailLoading] = useState(false)
  const [showReasoning, setShowReasoning] = useState(false)
  const [runDetailErr, setRunDetailErr] = useState<string | null>(null)

  const [patch, setPatch] = useState<ReviewPatch | null>(null)
  const [patchLoading, setPatchLoading] = useState(false)
  const [patchErr, setPatchErr] = useState<string | null>(null)
  const [guidance, setGuidance] = useState('')
  const [queriesText, setQueriesText] = useState('')
  const [jobId, setJobId] = useState<string | null>(null)

  const itemQuery = useQuery({
    queryKey: qk.items.detail(itemId || ''),
    queryFn: () => clientApi.itemDetail(itemId as string),
    enabled: !!itemId,
  })
  const data = itemQuery.data ?? null
  const error = itemQuery.error

  const meQuery = useQuery({
    queryKey: qk.me(),
    queryFn: () => clientApi.me(),
    staleTime: 60_000,
  })
  const isStaff = !!meQuery.data?.is_cenva_staff

  // Derived values needed by hooks below (must be before early return)
  const it = data?.item
  const latest = data?.latest
  const ps = latest?.prompt_safe_update_json
  const internal = latest?.internal_update_json
  const latestRunId = latest?.run_id as number | undefined
  const reasoningLineage = (data as any)?.reasoning_lineage as
    | {
        reasoning_as_of?: string | null
        headline_as_of?: string | null
        is_carried_forward?: boolean
        whats_changed?: { as_of?: string | null; summary?: string | null; key_points?: string[] } | null
      }
    | undefined
  const evidence = (data as any)?.evidence as
    | {
        evidence_tier?: string | null
        confidence_interval?: [number, number] | null
        ci_width?: number | null
        source_classes?: string[] | null
        sources_count?: number | null
        rationale?: string | null
        calibration_label?: string | null
        calibration_note?: string | null
      }
    | undefined

  const marketLive = data?.market_live
  const edgeLive = data?.edge_live
  const sourcesDetail = ps?._meta?.sources_detail as any[] | undefined
  const scoredQuestion = data?.scored_question || {}
  const resolvedStatus = String(scoredQuestion?.status || '').toLowerCase()
  const resolutionOutcome = String(scoredQuestion?.resolution || '').trim()
  const scenario = internal?.scenario || ps?.scenario || null
  const scenarioGuidance = scenario && typeof scenario === 'object' ? (scenario.conditioning_guidance || {}) : {}
  // The latest update may be tagged with a scenario_id (stamped onto the
  // dossier update row); if so, the <ScenarioTracks> card highlights that
  // row as the "primary" track so the header pill and the tracks card
  // agree on which forecast is the current one.
  const primaryScenarioId: string | null | undefined = (() => {
    const sid = latest?.scenario_id ?? internal?.scenario_id ?? scenario?.scenario_id ?? null
    if (typeof sid === 'string' && sid) return sid
    // No scenario on the latest update — baseline is primary.
    if (!scenario) return null
    // Scenario payload exists but no id could be resolved: don't highlight.
    return undefined
  })()

  // Phase H: if a primary scenario exists, fetch its current status so
  // the pill + a banner can reflect realized/disproven states. Keep this
  // a separate, low-priority query so it never blocks the forecast view.
  const scenarioLive = useQuery({
    queryKey: primaryScenarioId ? qk.scenarios.detail(primaryScenarioId) : ['scenarios', 'detail', 'disabled'],
    queryFn: () => clientApi.getScenario(primaryScenarioId as string),
    enabled: typeof primaryScenarioId === 'string' && !!primaryScenarioId,
    staleTime: 30_000,
  })
  const scenarioStatus = (scenarioLive.data?.scenario?.status || '').toLowerCase()
  const scenarioResolved = scenarioStatus === 'realized' || scenarioStatus === 'disproven'

  const prob = internal?.probability
  const mktProb = marketLive?.prob
  const historyRows = (data?.history || []) as any[]
  const probHistory = historyRows
    .map((h: any) => h?.internal_update_json?.probability)
    .filter((p: any) => typeof p === 'number')
    .reverse() // oldest first for sparkline
  const probPrev = probHistory.length >= 2 ? probHistory[probHistory.length - 2] : null
  const delta = typeof prob === 'number' && typeof probPrev === 'number' ? prob - probPrev : null

  async function refreshPatch() {
    if (!isStaff) return
    if (typeof latestRunId !== 'number') return
    setPatchLoading(true)
    setPatchErr(null)
    try {
      const res = await clientApi.listReviewPatches({ reviewed_run_id: latestRunId, limit: 5 })
      const p = Array.isArray(res?.patches) && res.patches.length > 0 ? res.patches[0] : null
      setPatch(p)
      setGuidance(String(p?.prompt_safe_guidance || ''))
      setQueriesText(Array.isArray(p?.suggested_search_queries) ? p!.suggested_search_queries!.join('\n') : '')
    } catch (e: any) {
      setPatchErr(e?.message || 'Failed to load patch')
    } finally {
      setPatchLoading(false)
    }
  }

  useEffect(() => {
    if (!isStaff) return
    if (typeof latestRunId !== 'number') return
    refreshPatch()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStaff, latestRunId])

  // Poll the rerun/review job until it terminates. Uses React Query
  // `refetchInterval` so it pauses while the tab is hidden.
  const [jobSseOnline, setJobSseOnline] = useState(false)
  const jobQuery = useQuery({
    queryKey: qk.jobs.detail(jobId || ''),
    queryFn: () => clientApi.jobStatus(jobId as string),
    enabled: !!jobId,
    refetchInterval: (query) => {
      const st = (query.state.data as any)?.status
      if (st === 'succeeded' || st === 'failed') return false
      return jobSseOnline ? 15_000 : 2_000
    },
    staleTime: 0,
  })
  const jobStatus = jobQuery.data ?? null

  // Live stream of the rerun job status. When the stream is healthy we
  // widen the poll to 15s (safety net); the stream itself drives the
  // UI updates by writing directly to the react-query cache.
  useEffect(() => {
    if (!jobId) return
    const key = qk.jobs.detail(jobId)
    const stop = streamSse<JobStatus>(
      `/v1/jobs/${encodeURIComponent(jobId)}/stream`,
      {
        onEvent: (evt) => {
          if (evt.type === 'status' && evt.data) {
            setJobSseOnline(true)
            queryClient.setQueryData(key, evt.data)
          }
        },
        onClose: () => {
          setJobSseOnline(false)
        },
      },
    )
    return () => stop()
  }, [jobId, queryClient])

  // When the job terminates, refresh derived state (patch + item detail).
  useEffect(() => {
    const st = (jobStatus as any)?.status
    if (st !== 'succeeded' && st !== 'failed') return
    refreshPatch()
    if (itemId) queryClient.invalidateQueries({ queryKey: qk.items.detail(itemId) })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobStatus?.status])

  // Loading state — all hooks are above this point
  if (!data && !error) {
    return (
      <div className="container">
        <Topbar subtitle="Item detail" />
        <div className="grid" style={{ marginTop: 16 }}>
          <Skeleton variant="card" count={3} />
        </div>
      </div>
    )
  }

  const keyPoints = Array.isArray(ps?.key_points) ? (ps.key_points as any[]) : []
  const drivers = Array.isArray(ps?.drivers) ? (ps.drivers as any[]) : []
  const uncertainties = Array.isArray(ps?.uncertainties) ? (ps.uncertainties as any[]) : []
  const triggers = Array.isArray(ps?.update_triggers) ? (ps.update_triggers as any[]) : []

  function toggleReasoning() {
    if (showReasoning) {
      setShowReasoning(false)
      return
    }
    if (!latestRunId) return
    setShowReasoning(true)
    if (runDetail?.run_id === latestRunId) return
    setRunDetailLoading(true)
    setRunDetailErr(null)
    clientApi.runDetail(latestRunId)
      .then((d) => { setRunDetail(d) })
      .catch((err) => {
        console.error('runDetail fetch failed:', err)
        setRunDetail(null)
        setRunDetailErr(err?.message || 'Unknown error')
      })
      .finally(() => setRunDetailLoading(false))
  }

  return (
    <RenderBoundary label="ItemDetail">
    <div className="container">
      <Topbar subtitle="Item detail" />
      {/* Deep links carry no referrer state; the API resolves the owning
          workspace so the back link always lands somewhere meaningful. */}
      <BackLink
        defaultTo={data?.workspace_ref?.workspace_id ? `/workspaces/${data.workspace_ref.workspace_id}` : '/workspaces'}
        defaultLabel={data?.workspace_ref?.title || 'Workspaces'}
      />

      <div className="grid">
        {/* Header card */}
        <div className="item-card">
          <div className="item-card-header">
            <div style={{ flex: 1, minWidth: 0 }}>
              {(() => {
                // Stored titles are often a mid-word truncation of the
                // canonical question; when the canonical text simply extends
                // the title, show the full question once instead of the cut
                // followed by its own repeat in the "Event:" line.
                const rawTitle = String(it?.label_override || data?.scored_question?.title || it?.item_id || '')
                const canonical = String(data?.canonical_question?.title || '')
                const norm = (s: string) => s.replace(/\s+/g, ' ').trim()
                const rawCore = norm(rawTitle).replace(/[.…]+$/, '')
                const canonicalExtendsTitle = Boolean(
                  canonical && rawCore && norm(canonical).toLowerCase().startsWith(rawCore.toLowerCase()),
                )
                const headerTitle = canonicalExtendsTitle ? canonical : rawTitle
                const showEventLine = Boolean(canonical) && !canonicalExtendsTitle && canonical !== rawTitle
                return (
                  <>
                    <h2 style={{ margin: 0, overflowWrap: 'break-word', wordBreak: 'break-word' }}>{headerTitle}</h2>
                    {showEventLine && (
                      <div className="muted" style={{ marginTop: 4 }}>
                        Event:{' '}
                        {data?.canonical_question?.url ? (
                          <a href={data.canonical_question.url} target="_blank" rel="noreferrer">
                            {canonical}
                          </a>
                        ) : (
                          <span>{canonical}</span>
                        )}
                      </div>
                    )}
                  </>
                )
              })()}
            </div>
            <div className="row" style={{ gap: 10, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              {probHistory.length >= 2 && <Sparkline values={probHistory} width={120} height={36} />}
              {it?.item_id && <DownloadReportButton kind="question" targetId={it.item_id} />}
            </div>
          </div>

          <div style={{ marginTop: 12 }}>
            <div className="item-card-prob-row">
              <div style={{ flex: 1 }}>
                <ProbBar prob={prob} marketProb={mktProb} />
              </div>
              <Delta value={delta} />
            </div>
          </div>

          <div className="row" style={{ marginTop: 10, flexWrap: 'wrap', gap: 6 }}>
            {resolvedStatus === 'resolved' && (
              <span className="pill ok">resolved{resolutionOutcome ? ` · ${resolutionOutcome}` : ''}</span>
            )}
            {resolvedStatus === 'invalid' && (
              <span className="pill bad">invalid</span>
            )}
            <span className="pill ok" title={scenario?.label ? `Conditional on scenario: ${scenario.label}` : undefined}>
              our {fmtProb(prob)}
              {scenario ? ' · conditional' : ''}
            </span>
            {scenario?.label && (primaryScenarioId ? (
              <Link
                to={`/universe/scenarios/${primaryScenarioId}`}
                className="pill"
                title="Open scenario detail"
                style={{ textDecoration: 'none' }}
              >
                scenario: {String(scenario.label).slice(0, 40)}{String(scenario.label).length > 40 ? '…' : ''}
              </Link>
            ) : (
              <span className="pill" title="Active scenario">
                scenario: {String(scenario.label).slice(0, 40)}{String(scenario.label).length > 40 ? '…' : ''}
              </span>
            ))}
            {scenarioResolved && (
              <span
                className={scenarioStatus === 'realized' ? 'pill ok' : 'pill bad'}
                title={
                  scenarioStatus === 'realized'
                    ? 'The assumptions in this scenario have come true'
                    : 'The assumptions in this scenario are no longer possible'
                }
              >
                scenario {scenarioStatus}
              </span>
            )}
            {typeof mktProb === 'number' && <span className="pill">mkt {fmtProb(mktProb)}</span>}
            {typeof edgeLive === 'number' && (
              <span className={edgeLive >= 0 ? 'pill ok' : 'pill bad'}>edge {fmtEdge(edgeLive)}</span>
            )}
            {typeof evidence?.evidence_tier === 'string' && (
              <span
                className={`pill ${
                  evidence.evidence_tier === 'high'
                    ? 'ok'
                    : evidence.evidence_tier === 'low'
                    ? 'bad'
                    : ''
                }`}
                title="Forecastability gate: how well-evidenced this forecast is (source breadth, domain diversity, query success)."
              >
                evidence: {evidence.evidence_tier}
              </span>
            )}
            {Array.isArray(evidence?.confidence_interval) && evidence.confidence_interval.length === 2 && (
              <span
                className="pill"
                title="Confidence interval around the point probability — narrower is more resolved."
              >
                CI {fmtProb(evidence.confidence_interval[0])}–{fmtProb(evidence.confidence_interval[1])}
              </span>
            )}
            {latest?.as_of && (
              <span className="pill">{timeAgo(latest.as_of)}</span>
            )}
            {typeof evidence?.calibration_label === 'string' && evidence.calibration_label && (
              <span className="pill ok" title={evidence.calibration_note || ''}>{evidence.calibration_label}</span>
            )}
          </div>

          {typeof evidence?.rationale === 'string' && evidence.rationale && (
            <div
              className="muted"
              style={{
                marginTop: 8,
                fontSize: 12,
                lineHeight: 1.5,
                padding: '8px 10px',
                borderRadius: 8,
                background: 'rgba(148, 163, 184, 0.08)',
                border: '1px solid rgba(148, 163, 184, 0.18)',
              }}
            >
              <span style={{ fontWeight: 700 }}>Why this confidence: </span>
              {evidence.rationale}
            </div>
          )}

          {typeof marketLive?.bid === 'number' && (
            <div className="muted" style={{ marginTop: 4, fontSize: 12 }}>
              Bid: {fmtProb(marketLive.bid)} · Ask: {fmtProb(marketLive?.ask)}
              {typeof marketLive?.spread_bps === 'number' ? ` · Spread: ${marketLive.spread_bps} bps` : ''}
            </div>
          )}
          {scenarioResolved && primaryScenarioId && (
            <div
              style={{
                marginTop: 10,
                padding: '10px 12px',
                borderRadius: 10,
                background:
                  scenarioStatus === 'realized'
                    ? 'rgba(34, 197, 94, 0.08)'
                    : 'rgba(239, 68, 68, 0.08)',
                border:
                  scenarioStatus === 'realized'
                    ? '1px solid rgba(34, 197, 94, 0.22)'
                    : '1px solid rgba(239, 68, 68, 0.22)',
                fontSize: 13,
                lineHeight: 1.5,
              }}
              role="note"
              aria-label={`Scenario ${scenarioStatus}`}
            >
              <strong style={{ textTransform: 'capitalize' }}>{scenarioStatus}:</strong>{' '}
              {scenarioStatus === 'realized'
                ? 'the assumptions attached to this forecast have been confirmed. Any probability below treats them as true, which now matches reality.'
                : 'the assumptions attached to this forecast have been ruled out. The conditional probability below no longer reflects a live scenario — consider re-asking this question without the scenario.'}
              {' '}
              <Link to={`/universe/scenarios/${primaryScenarioId}`}>Open scenario.</Link>
            </div>
          )}
        </div>

        {/* Scenario tracks (baseline + per-scenario forecast lines). */}
        {itemId && <ScenarioTracks itemId={itemId} primaryScenarioId={primaryScenarioId} />}

        {/* Scenario dossier summary — mirrors the forecast "Analysis" card but
            scoped to the scenario's shared brief. Only shown when this item is
            a conditional forecast with a known scenario id. */}
        {typeof primaryScenarioId === 'string' && primaryScenarioId && (
          <ScenarioDossierSummaryCard scenarioId={primaryScenarioId} />
        )}

        {/* Analysis card */}
        {ps?.summary && (
          <div className="card">
            <h2>Analysis</h2>
            {scenario && (
              <div style={{ marginBottom: 12, padding: '10px 12px', borderRadius: 10, background: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.18)' }}>
                <div style={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase', color: 'var(--accent)', marginBottom: 6 }}>
                  Conditional Assumptions
                </div>
                {scenario.label && <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 6 }}>{scenario.label}</div>}
                {Array.isArray(scenario.assumptions) && scenario.assumptions.length > 0 && (
                  <ul style={{ margin: '0 0 0 18px', padding: 0, fontSize: 12, lineHeight: 1.55 }}>
                    {scenario.assumptions.map((a: any, i: number) => (
                      <li key={i}>{typeof a === 'string' ? a : (a?.text || String(a))}</li>
                    ))}
                  </ul>
                )}
                {scenarioGuidance.conditioning_summary && (
                  <div style={{ marginTop: 10, fontSize: 12, lineHeight: 1.55 }}>
                    {scenarioGuidance.conditioning_summary}
                  </div>
                )}
                {Array.isArray(scenarioGuidance.removed_uncertainties) && scenarioGuidance.removed_uncertainties.length > 0 && (
                  <div style={{ marginTop: 10 }}>
                    <div style={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 4 }}>
                      What these assumptions lock in
                    </div>
                    <ul style={{ margin: '0 0 0 18px', padding: 0, fontSize: 12, lineHeight: 1.55 }}>
                      {scenarioGuidance.removed_uncertainties.slice(0, 5).map((x: any, i: number) => (
                        <li key={i}>{String(x)}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {Array.isArray(scenarioGuidance.implication_prompts) && scenarioGuidance.implication_prompts.length > 0 && (
                  <div style={{ marginTop: 10 }}>
                    <div style={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 4 }}>
                      Why the forecast changes under these assumptions
                    </div>
                    <ul style={{ margin: '0 0 0 18px', padding: 0, fontSize: 12, lineHeight: 1.55 }}>
                      {scenarioGuidance.implication_prompts.slice(0, 6).map((x: any, i: number) => (
                        <li key={i}>{String(x)}</li>
                      ))}
                    </ul>
                  </div>
                )}
                <div className="muted" style={{ fontSize: 11, marginTop: 8 }}>
                  This forecast is conditional on the assumptions above being true.
                </div>
              </div>
            )}
            <div style={{ lineHeight: 1.5 }}>{ps.summary}</div>

            {Array.isArray(sourcesDetail) && sourcesDetail.length > 0 && (
              <div className="stack" style={{ marginTop: 14 }}>
                <div className="sectionTitle" style={{ fontSize: 13 }}>Sources</div>
                {sourcesDetail.slice(0, 6).map((s: any, idx: number) => {
                  const snippet = cleanSourceSnippet(s?.snippet, s?.title)
                  return (
                    <a key={`${s?.url || idx}`} className="sourceRow" href={s?.url} target="_blank" rel="noreferrer">
                      <div className="sourceTitle">{s?.title || 'Source'}</div>
                      {snippet && <div className="muted" style={{ fontSize: 12 }}>{snippet}</div>}
                    </a>
                  )
                })}
              </div>
            )}

            {!sourcesDetail?.length && Array.isArray(ps?.sources) && ps.sources.length > 0 && (
              <div className="row" style={{ marginTop: 10 }}>
                {ps.sources.slice(0, 5).map((u: string, i: number) => (
                  <a key={i} className="pill" href={u} target="_blank" rel="noreferrer">
                    source
                  </a>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Forecast reasoning (agent-level detail) + integrated analyst review */}
        {typeof latestRunId === 'number' && (
          <div className="card">
            <div
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}
              onClick={toggleReasoning}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleReasoning() }
              }}
              role="button"
              tabIndex={0}
              aria-expanded={showReasoning}
              aria-controls="forecast-reasoning-panel"
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <h2 style={{ margin: 0 }}>Forecast Reasoning</h2>
                {isStaff && patch?.patch_id && (
                  <span className="pill ok" style={{ fontSize: 10, padding: '2px 6px' }}>review</span>
                )}
              </div>
              <span className="muted" style={{ fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                {showReasoning ? 'hide' : 'show'}
                <Chevron open={showReasoning} />
              </span>
            </div>
            {showReasoning && (
              <div id="forecast-reasoning-panel">
                {reasoningLineage?.reasoning_as_of && (
                  <div className="muted" style={{ fontSize: 12, marginBottom: 10, padding: '6px 10px', borderRadius: 8, background: 'rgba(148,163,184,0.08)', border: '1px solid rgba(148,163,184,0.18)' }}>
                    Agent reasoning below is <strong>as of the initial forecast</strong> · {fmtTs(reasoningLineage.reasoning_as_of)}
                    {reasoningLineage.is_carried_forward && reasoningLineage.headline_as_of && (
                      <> — the headline reflects a later update ({fmtTs(reasoningLineage.headline_as_of)}). See “What’s changed since” below.</>
                    )}
                  </div>
                )}
                {runDetailLoading
                  ? <div className="muted" style={{ fontSize: 12, padding: '10px 0' }}>Loading agent reasoning...</div>
                  : runDetail
                    ? <RenderBoundary label="ForecastReasoning"><ForecastReasoningPanel detail={runDetail} /></RenderBoundary>
                    : <div style={{ fontSize: 12, padding: '10px 0', color: 'var(--warn, #f59e0b)' }}>
                        Could not load reasoning{runDetailErr ? `: ${runDetailErr}` : ''}
                      </div>
                }

                {/* Staff-only: analyst review section (lazy-loaded) */}
                {isStaff && (
                  <Suspense fallback={<div className="muted" style={{ fontSize: 12, padding: '10px 0' }}>Loading review tools…</div>}>
                    <StaffReviewPanel
                      itemId={itemId}
                      latestRunId={latestRunId}
                      patch={patch}
                      setPatch={setPatch}
                      patchLoading={patchLoading}
                      setPatchLoading={setPatchLoading}
                      patchErr={patchErr}
                      setPatchErr={setPatchErr}
                      guidance={guidance}
                      setGuidance={setGuidance}
                      queriesText={queriesText}
                      setQueriesText={setQueriesText}
                      refreshPatch={refreshPatch}
                      jobId={jobId}
                      setJobId={setJobId}
                      jobStatus={jobStatus}
                      onInvalidateJob={(newJobId) => queryClient.removeQueries({ queryKey: qk.jobs.detail(newJobId) })}
                    />
                  </Suspense>
                )}
              </div>
            )}
          </div>
        )}

        {/* What's changed since the initial forecast (only when a substantive
            update post-dates the shown reasoning — keeps the headline honest). */}
        {reasoningLineage?.whats_changed && (reasoningLineage.whats_changed.summary || (reasoningLineage.whats_changed.key_points || []).length > 0) && (
          <div className="card">
            <div className="sectionTitle">
              What’s changed since{reasoningLineage.whats_changed.as_of ? ` · ${fmtTs(reasoningLineage.whats_changed.as_of)}` : ''}
            </div>
            {reasoningLineage.whats_changed.summary && (
              <p style={{ marginTop: 8 }}>{reasoningLineage.whats_changed.summary}</p>
            )}
            {(reasoningLineage.whats_changed.key_points || []).length > 0 && (
              <ul className="bullets">
                {(reasoningLineage.whats_changed.key_points as string[]).slice(0, 5).map((x, idx) => (
                  <li key={idx}>{x}</li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Key points + drivers/uncertainties */}
        {(keyPoints.length > 0 || drivers.length > 0 || uncertainties.length > 0) && (
          <div className="card">
            {keyPoints.length > 0 && (
              <div>
                <div className="sectionTitle">Key points</div>
                <ul className="bullets">
                  {keyPoints.slice(0, 7).map((x: any, idx: number) => (
                    <li key={idx}>{typeof x === 'string' ? x : renderStructuredItem(x)}</li>
                  ))}
                </ul>
              </div>
            )}

            {(drivers.length > 0 || uncertainties.length > 0) && (
              <div className="twoCol" style={{ marginTop: keyPoints.length > 0 ? 14 : 0 }}>
                {drivers.length > 0 && (
                  <div>
                    <div className="sectionTitle">Drivers</div>
                    <ul className="bullets">
                      {drivers.slice(0, 8).map((raw: any, idx: number) => {
                        const x = tryParseJson(raw)
                        return (
                          <li key={idx}>
                            {typeof x === 'object' && x !== null
                              ? <><strong>{str(x.driver || x.name || '')}</strong>{(x.direction || x.detail || x.description) ? ` — ${str(x.direction || x.detail || x.description)}` : ''}</>
                              : String(raw)}
                          </li>
                        )
                      })}
                    </ul>
                  </div>
                )}
                {uncertainties.length > 0 && (
                  <div>
                    <div className="sectionTitle">Uncertainties</div>
                    <ul className="bullets">
                      {uncertainties.slice(0, 8).map((raw: any, idx: number) => {
                        const x = tryParseJson(raw)
                        return (
                          <li key={idx}>
                            {typeof x === 'object' && x !== null
                              ? <><strong>{str(x.uncertainty || x.name || '')}</strong>{(x.impact || x.detail || x.description) ? ` — ${str(x.impact || x.detail || x.description)}` : ''}</>
                              : String(raw)}
                          </li>
                        )
                      })}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Update triggers */}
        {triggers.length > 0 && (
          <div className="card">
            <h2>Update triggers</h2>
            <div className="tableWrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Trigger</th>
                    <th>Query</th>
                    <th>Why</th>
                  </tr>
                </thead>
                <tbody>
                  {triggers.slice(0, 6).map((t: any, idx: number) => (
                    <tr key={idx}>
                      <td>{t?.trigger ? String(t.trigger) : ''}</td>
                      <td className="mono">{t?.query ? String(t.query) : ''}</td>
                      <td className="muted">{t?.why ? String(t.why) : ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* History */}
        {historyRows.length > 0 && (
          <div className="card">
            <h2>History</h2>
            <div className="stack" style={{ gap: 0 }}>
              {historyRows.slice(0, 12).map((h: any, idx: number) => (
                <div key={`${h?.dossier_update_id || idx}`} className="signal-row">
                  <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline', gap: 12 }}>
                    <div className="muted" style={{ fontSize: 12 }}>
                      {fmtTs(h?.as_of)}
                    </div>
                    <div className="mono" style={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                      {fmtProb(h?.internal_update_json?.probability)}
                    </div>
                  </div>
                  <div style={{ marginTop: 6, fontSize: 13, lineHeight: 1.55, overflowWrap: 'break-word', wordBreak: 'break-word' }}>
                    {h?.prompt_safe_update_json?.summary || ''}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Contract (collapsible) */}
        {data?.contract && Object.keys(data.contract).length > 0 && (
          <div className="card">
            <div
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}
              onClick={() => setShowContract(!showContract)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setShowContract(!showContract) }
              }}
              role="button"
              tabIndex={0}
              aria-expanded={showContract}
              aria-controls="contract-panel"
            >
              <h2 style={{ margin: 0 }}>Contract</h2>
              <span className="muted" style={{ fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                {showContract ? 'hide' : 'show'}
                <Chevron open={showContract} />
              </span>
            </div>
            {showContract && (
              <pre id="contract-panel" className="mono" style={{ margin: '10px 0 0', whiteSpace: 'pre-wrap', fontSize: 12 }}>
                {JSON.stringify(data.contract, null, 2)}
              </pre>
            )}
          </div>
        )}

        {error && <ErrorBox error={error} />}
      </div>
    </div>
    </RenderBoundary>
  )
}
